import {
    BackSide,
    ColladaLoader,
    CubeCamera,
    CubeGeometry,
    DoubleSide,
    FBXLoader,
    FogExp2,
    FrontSide,
    GLTFLoader,
    DRACOLoader,
    JSONLoader,
    LinearFilter,
    LoadingManager,
    Loader,
    Mesh,
    MeshBasicMaterial,
    Object3D,
    OBJLoader2,
    PerspectiveCamera,
    PlaneGeometry,
    RGBFormat,
    Scene,
    SkeletonHelper,
    TextureLoader,
    VideoTexture
} from 'da-three';
import EventDispatcher from './../service/EventDispatcher.js';

export default class SceneManager{
    constructor(mediator,config){
        this.config = config;
        this.mediator = mediator;
        this.scene = new Scene();
        this.skeletonHelpers = [];
        
        this.dispatcher = new EventDispatcher();
        this.dispatcher.setEventTypes([
            'beforeObjectLoad',
            'objectComplete',
            'objectProgress'
        ]);
        
        this.loadCamera();
        this.scene.fog = new FogExp2( this.config.surrounding.fogColor, 0 );
        
        //Object which is rotated	
        this.sceneHinge = new Object3D();
        this.scene.add(this.sceneHinge);
        
        this.loadSkybox();
        this.fileLoader = null;
        this.mediator.addEventListener('beforeWindowResize',this.onWindowResize.bind(this));
    }
    
    
    
    addObjectToGroup(object){
        let maxAnisotropy = this.renderMan.renderer.capabilities.getMaxAnisotropy();

        object.position.y = 0; 

        object.traverse(function (node) {
            
            if( node.material ) {
                node.material.side = DoubleSide;
            }
            if ( node instanceof Mesh ) {
                if (node.material.map !== null && node.material.map !== undefined){
                    node.material.map.anisotropy = maxAnisotropy;
                }
                if (node.material.envMap !== null && node.material.envMap !== undefined) {
                    node.material.envMap.anisotropy = maxAnisotropy;
                }
                node.castShadow = true;
                node.receiveShadow = true;
                node.geometry.computeBoundingBox();
            }
        });
        this.group.add(object);
    }
    
    
    
    callbackProgress = function( progress ) {
        this.dispatcher.dispatch('objectProgress',progress);
    }
    
    
    
    getCompoundBoundingBox(object3D) {
        let box = null;
        
        object3D.traverse(function (obj3D) {
            let geometry = obj3D.geometry;
            if (geometry === undefined) {
                return;
            }
            let bb = geometry.boundingBox;
            if (box === null) {
                box = bb;
            } else {
                box.union(bb);
            }
        });
        
        let bboxCenter = box.getCenter ();
        let cubeX = box.max.x - box.min.x;
        let cubeY = box.max.y - box.min.y;
        let cubeZ = box.max.z - box.min.z;

        //TODO: Calculate bounding box properly instead of just scaling visible cube
        if (this.config.object.scale !== undefined){
            let scale = this.config.object.scale;
            cubeX *= scale;
            cubeY *= scale;
            cubeZ *= scale;
        }

        
        let cubeGeom = new CubeGeometry(cubeX, cubeY, cubeZ, 1, 1, 1);


        let boundingBox = new Mesh(cubeGeom, new MeshBasicMaterial( { color: 0xff0000, wireframe: true, transparent: false }));
        boundingBox.material.opacity = 0;
        boundingBox.visible = false;
        boundingBox.translateX(bboxCenter.x);
        boundingBox.translateY(bboxCenter.y);
        boundingBox.translateZ(bboxCenter.z);
        this.sceneHinge.add(boundingBox);
        if (this.config.tech.debug) {
            boundingBox.visible = true;
            console.log('DEBUG ===== Bounding box:',boundingBox);
        }

        return box;
    }
    
    
    
    init(renderMan,matMan){
        this.renderMan = renderMan;
        this.matMan = matMan;
    }
    
    
    
    loadCamera() {
        this.camera = new PerspectiveCamera( 
            this.config.camera.VIEW_ANGLE, 
            window.innerWidth/window.innerHeight, 
            this.config.camera.NEAR, 
            this.config.camera.FAR
        );
        this.scene.add(this.camera);
        this.camera.position.set(0,0,6);
        this.camera.lookAt(0,0,-6);
    }
    
    
    
    loadDAE(){     
        let objToLoad 	= this.config.project.basedir + this.config.project.folder + this.config.object.name + '.dae';
        
        let loader = new ColladaLoader();
        let scope = this;
        loader.options.convertUpAxis = true;
        loader.load(objToLoad , function ( collada ) {
            let animations = collada.animations;
            let object = collada.scene;
            object.animations = animations;
            
            scope.loadLastCompleteHandler(object);
        } );
    }
    
    
    
    loadFBX(){
        let manager = new LoadingManager();
        manager.onProgress = function( item, loaded, total ) {
            console.log( item, loaded, total );
        };
        
        let onProgress = function( xhr ) {
            if ( xhr.lengthComputable ) {
                let percentComplete = xhr.loaded / xhr.total * 100;
                console.log( Math.round( percentComplete, 2 ) + '% downloaded' );
            }
        };
        let onError = function( xhr ) {
            console.error( xhr );
        };
        let objToLoad 	= this.config.project.basedir + this.config.project.folder + this.config.object.name + '.fbx';
        let loader = new FBXLoader( manager );
        let scope = this;
        loader.load( objToLoad, (object) => {
            scope.loadLastCompleteHandler(object);
        }, onProgress, onError );
    }
    
    
    
    loadGLTF(){
        let zipIt = '.gz';
        if ((/safari/i).test(navigator.userAgent) && !(/mobile/i).test(navigator.userAgent)){
            zipIt = '';
            if (this.config.tech.debug) console.log("Detected safari desktop!");
        }
        let onProgress = function( xhr ) {
            if ( xhr.lengthComputable ) {
                let percentComplete = xhr.loaded / xhr.total * 100;
                console.log( Math.round( percentComplete, 2 ) + '% downloaded' );
            }
        };
        let onError = function( xhr ) {
            console.error( xhr );
        };
        let objToLoad = this.config.project.basedir + this.config.project.folder + this.config.object.name + '.gltf';// + zipIt;
        let manager = new LoadingManager();
        let loader = new GLTFLoader(manager);
        DRACOLoader.setDecoderPath(this.config.project.basedir+this.config.project.static+'libs/draco/gltf/');
        loader.setDRACOLoader(new DRACOLoader());
        let scope = this;
        
        if (this.fileLoader !== null) {
            console.log("Using other FileLoader");
            var path = loader.path !== undefined ? loader.path : Loader.prototype.extractUrlBase(objToLoad);
            this.fileLoader.setResponseType('arrayBuffer');
            this.fileLoader.load(objToLoad, (data) => {
                try {
                    loader.parse(data, path, gltf => { 
                        let object = gltf.scene;
                        object.animations = gltf.animations;
                        scope.loadLastCompleteHandler(object);
                    }, onError);
                } catch (e) {
                    onError(e);
                }
            }, onProgress, onError);
        } else {
            loader.load(objToLoad, (gltf) => {
                let object = gltf.scene;
                object.animations = gltf.animations;

                scope.loadLastCompleteHandler(object);
            }, onProgress, onError);
        }
    }


    loadJSON(){
        let loader = new JSONLoader();
        let objToLoad 	= this.config.project.basedir + this.config.project.folder + this.config.object.name + '.json';
        let scope = this;
        loader.load( objToLoad, function ( geometry, materials ) {
            // adjust color a bit
            var material = materials[ 0 ];
            material.morphTargets = true;
            var object = new Mesh( geometry, materials );
            object.matrixAutoUpdate = false;
            object.updateMatrix();
            object.animations = geometry.animations;

            scope.loadLastCompleteHandler(object);
        } );
    }
    
    
    
    loadOBJ(){
        let loader = new OBJLoader2();
        
        ///*
        let materials = this.matMan.getMaterials().reduce((p, c) => {
            p[c.dbID] = c;
            return p;
        }, []);
        let slots = this.config.object.slots.reduce((p, c) => {
            p[c.name] = materials[c.materialID];
            return p;
        }, []);
        loader.setMaterials(slots);
        //*/
        let objToLoad 	= this.config.project.basedir + this.config.project.folder + this.config.object.name;
        let partsToLoad = this.config.object.parts;
        let scope = this;
        
        if (this.fileLoader) {
            console.log(this.fileLoader);
        }

        //Loading parts
        for(let i = 0; i < partsToLoad; i++) {
            if(i< 10) {
                i = "000"+i;
            } else if(i < 100) {
                i = "00"+i;
            } else if(i < 1000) {
                i = "0"+i;
            }

            // TODO fix GZ
            if (i < partsToLoad-1){
                loader.load(objToLoad + "_" + i +".obj", function(object){
                    scope.loadCompleteHandler(object.detail.loaderRootNode);
                },this.callbackProgress.bind(this));
            }else{
                loader.load(objToLoad + "_" + i +".obj", function(object){
                    scope.loadLastCompleteHandler(object.detail.loaderRootNode);
                },this.callbackProgress.bind(this));
            }
        }
    }
    
    
    
    loadObjFile() {
        if (this.config.tech.debug) console.log('DEBUG ===== loading object...');
        this.dispatcher.dispatch('beforeObjectLoad');
        this.group = new Object3D();
        
        switch(this.config.object.type){
            case 'dae':
                this.loadDAE();
                break;
            case 'fbx':
                this.loadFBX();
                break;
            case 'gltf':
                this.loadGLTF();
                break;
            case 'json':
                this.loadJSON();
                break;    
            case 'obj':
                this.loadOBJ();
                break;
            default:
                console.error('3D file type not reconized! Use DAE, FBX, glTF, JSON or OBJ.');
                break;
        }

        if (this.config.object.scale !== undefined){
            let scale = this.config.object.scale;
            this.group.scale.set(scale,scale,scale);
        }
        this.sceneHinge.add( this.group );
    }
    
    
    
    loadCompleteHandler ( object ) {	
        this.addObjectToGroup(object);
    }
    
    
    
    loadLastCompleteHandler ( object ) {
        this.addObjectToGroup(object);
        if (this.config.tech.debug){
            object.traverse((node) => {
                if (node.isMesh && node.skeleton) {
                    const helper = new SkeletonHelper(node.skeleton.bones[0].parent);
                    helper.material.linewidth = 3;
                    this.scene.add(helper);
                    this.skeletonHelpers.push(helper);
                }
            });
        }
        
        
        this.group.boundingBox = this.getCompoundBoundingBox(this.group);

        this.dispatcher.dispatch('objectComplete',object);
        if (this.config.tech.debug) console.log('DEBUG ===== object done');
    }
    
    
    
    loadSkybox(){
        if (this.config.features.useSkyBox.active){
            //Environment reflection camera
            this.mirrorCubeCamera = new CubeCamera( 0.1, 5000, 512 );
            this.mirrorCubeCamera.position.set(this.sceneHinge.position);	
            this.scene.add( this.mirrorCubeCamera );

            this.loadVideo();

            let env = 'default';
            if (this.config.surrounding.environment !== undefined){
                env = this.config.surrounding.environment;
            }

            let imagePrefix = this.config.project.basedir+this.config.project.static+'textures/environments/'+env+'/';
            let directions  = ["pos-x", "neg-x", "pos-y", "neg-y", "pos-z", "neg-z"];
            let imageSuffix = ".jpg";
            let skyGeometry = new CubeGeometry( 5000, 5000, 5000 );	

            let materialArray = [];
            let texLoader = new TextureLoader();
            texLoader.crossOrigin = " ";
            for (let i = 0; i < 6; i++) {              
                materialArray.push(new MeshBasicMaterial({
                    map: texLoader.load(imagePrefix + directions[i] + imageSuffix),
                    side: BackSide
                }));
            }
                
            this.skyBox = new Mesh( skyGeometry, materialArray );
            this.sceneHinge.add( this.skyBox );
        }
        
    }
    
    
    
    loadVideo(){
        if (this.config.features.useVideo.active){
            if (this.config.tech.debug) console.log('DEBUG ===== loading video');
            this.video = document.getElementById( 'monitor' );

            this.videoImage = document.getElementById( 'videoImage' );
            this.videoImageContext = this.videoImage.getContext( '2d' );
            // background color if no video present
            this.videoImageContext.fillStyle = '#000000';
            this.videoImageContext.fillRect( 0, 0, this.videoImage.width, this.videoImage.height );

            this.videoTexture = new VideoTexture( this.videoImage );
            this.videoTexture.minFilter = LinearFilter;
            this.videoTexture.magFilter = LinearFilter;
            this.videoTexture.format = RGBFormat;

            let movieMaterial = new MeshBasicMaterial( { 
                map: this.videoTexture, 
                overdraw: true, 
                side: FrontSide , 
                transparent: true, 
                opacity: 0.7
            } );
            // the geometry on which the movie will be displayed;
            // movie image will be scaled to fit these dimensions.
            let movieGeometry = new PlaneGeometry( 1000, 1000, 1, 1 );
            this.movieScreen = new Mesh( movieGeometry, movieMaterial );	
            this.movieScreen.position.set(0,0,1500);
            this.movieScreen.rotation.y = Math.PI;
            this.camera.add(this.movieScreen);
            if (this.config.tech.debug) console.log('DEBUG ===== video done');
        }
    }
    
    
    
    onWindowResize(){
        let SCREEN_WIDTH = window.innerWidth;
        let SCREEN_HEIGHT = window.innerHeight;

        this.camera.aspect = SCREEN_WIDTH / SCREEN_HEIGHT;
        this.camera.updateProjectionMatrix();
    }
    
    
    
    setSkyBoxBrightness(value){
        if (this.config.features.useSkyBox.active){
            let mts = this.skyBox.material;
            for (let i=0;i<mts.length;i++){
                mts[i].color.r = value;
                mts[i].color.g = value;
                mts[i].color.b = value;
                mts[i].needsUpdate = true;
            }
        }
    }
    
    
    
    toggleSkyBox(value){
        if (this.config.features.useSkyBox.active){
            this.skyBox.visible = value;
        }
    }
}


// WEBPACK FOOTER //
// ./src/js/studio/manager/SceneManager.js